<script>
	import { LayerCake, ScaledSvg, Html } from "layercake";
	import { scaleOrdinal, scaleTime } from "d3-scale";

	import AxisX from "./components/AxisXInflationOnly.svelte";
	import AxisY from "./components/AxisYInflationOnly.svelte";
	import MultiLine from "./components/MultiLineInflationOnly.svelte";
	import Annotation from "./components/Annotation.svelte";
	import SharedTooltip from "./components/SharedTooltip.svelte";
	import LatestValue from "./components/LatestValue.svelte";
	import LastDataPointCircle from "./components/LastDataPointCircle.svelte";
	import LastDataPointLine from "./components/LastDataPointLine.svelte";

	import data from "./data_live/inflation_2015.csv";

	import { formatYear, formatOnlyMonthFull, stringToDate, formatFloat, formatDay, formatDayShort } from "./utils";

	// ///////////////////////////////////////////////////////////
	// constants and definitions
	// ///////////////////////////////////////////////////////////
	const frmtRate = formatFloat(0, "%");
	const frmtRateLong = formatFloat(1, "%");

	const tickYears = [2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];
	const xTicks = tickYears.map((y) => new Date(y, 0, 1));
	const yTicks = [2, 4, 6, 8, 10];

	const warStart = new Date(2022, 1, 24); // month is 0-indexed
	const valueAtWarStart = 6.1;
	const color = "#4545FA";

	// ///////////////////////////////////////////////////////////
	// process data
	// ///////////////////////////////////////////////////////////

	data.forEach((d) => {
		d.dateStr = d.date;
		d.value = +d.value;
		d.date = stringToDate(d.date, "yyyy-mm-dd", "-");
	});

	const latest = data.at(-1);
	xTicks.push(latest.date);

	// ///////////////////////////////////////////////////////////
	// Set what is our x key to separate it from the other series
	// ///////////////////////////////////////////////////////////
	const xKey = "date";
	const yKey = "value";
	const zKey = "variable";

	const yMax = Math.max(...data.map((d) => d[yKey]));

	const seriesNames = ["value"];
	const seriesColors = [color];

	// ///////////////////////////////////////////////////////////
	// Create a "long" format that is a grouped series of data points
	// Layer Cake uses this data structure and the key names
	// set in xKey, yKey and zKey to map your data into each scale.
	// ///////////////////////////////////////////////////////////
	const dataLong = seriesNames.map((key) => {
		return {
			[zKey]: key,
			values: data.map((d) => {
				d[xKey] = typeof d[xKey] === "string" ? Date.parse(d[xKey]) : d[xKey]; // Conditional required for sapper
				return {
					[yKey]: +d[key],
					[xKey]: d[xKey],
					[zKey]: key,
				};
			}),
		};
	});

	// ///////////////////////////////////////////////////////////
	// Make a flat array of the `values` of our nested series
	// we can pluck the field set from `yKey` from each item
	// in the array to measure the full extents
	// ///////////////////////////////////////////////////////////
	const flatten = (data) =>
		data.reduce((memo, group) => {
			return memo.concat(group.values);
		}, []);

	// ///////////////////////////////////////////////////////////
	// hover
	// ///////////////////////////////////////////////////////////

	export let hovered;
	const hoverOpacity = 0;
</script>

<div class="inflation">
	<div class="chart-container">
		<LayerCake
			ssr={true}
			percentRange={true}
			padding={{ top: 3, right: 0, bottom: 15, left: 0 }}
			x={xKey}
			y={yKey}
			z={zKey}
			xScale={scaleTime()}
			zScale={scaleOrdinal()}
			zDomain={seriesNames}
			zRange={seriesColors}
			flatData={flatten(dataLong)}
			yDomain={[0, yMax + 0.5]}
			data={dataLong}
		>
			<Html>
				<AxisY
					gridlines={true}
					baseline={false}
					ticks={yTicks}
					ticksPositionX={"left"}
					formatTick={(d, i) => frmtRate(d)}
				/>
				<AxisX
					gridlines={false}
					baseline={true}
					tickMarks={true}
					formatTick={(d, i) => formatYear(d)}
					formatTickSpecial={(d, i) => formatDay(d)}
					tickSpecialColor={color}
					snapTicks={false}
					yTick={10}
					tickHeight={5}
					tickOffsetY={4}
					ticks={xTicks}
				/>
			</Html>

			<ScaledSvg>
				<MultiLine strokeWidth={3} fill={false} />
			</ScaledSvg>

			<Html>
				<LastDataPointCircle opacity={hovered ? hoverOpacity : 1} />
				<!--
				<Annotation
					x={warStart}
					y={valueAtWarStart}
					label={"russische Invasion"}
					opacity={hovered ? hoverOpacity : 1}
				/>
				-->
				<LatestValue
					label="Inflationsrate"
					value={frmtRateLong(latest.value)}
					sublabel="im {formatOnlyMonthFull(latest.date)} {formatYear(latest.date)}"
					preliminary={latest.preliminary === "x"}
					{color}
					opacity={hovered ? hoverOpacity : 1}
				/>
				<SharedTooltip dataset={data} bind:d={hovered} w={140} offset={-15} let:d>
					<div class="tooltip">
						<div class="tooltip-date">{formatOnlyMonthFull(d.date)} {formatYear(d.date)}</div>
						<div class="tooltip-value" style="color: {color}">
							Inflationsrate {frmtRateLong(d.value)}
						</div>
					</div>
				</SharedTooltip>
			</Html>
		</LayerCake>
	</div>
</div>

<style lang="scss">
	@import "./main.scss";

	.inflation {
		height: 243px;
		// margin: 0 auto;
		font-size: $inflation-fs-l;
		line-height: $inflation-lh-l;
		font-family: $ff-sans;
		margin-bottom: 60px;
		display: flex;
		justify-content: center;
	}

	.chart-container {
		width: 100%;
		height: 100%;
	}

	.tooltip {
		padding: 7px 4px;
		text-align: center;
		font-size: 14px;
		line-height: 15px;
	}

	.tooltip-date {
		font-size: 12px;
		line-height: 14px;
		color: $grey;
	}

	.tooltip-value {
		font-weight: 500;
		margin-top: 4px;
	}

	// text-width
	@media (min-width: #{$text-width + 1px}) and (max-width: $raster-width) {
	}

	// not mobile
	@media (min-width: #{$text-width + 1px}) {
		.chart-container {
			width: 0.5 * $raster-width;
		}
	}

	// mobile
	@media (max-width: $text-width) {
		.chart-container {
			width: 100%;
		}
	}
</style>
